import { gsap } from "gsap";

export default class FiltersHandler {
    constructor() {
        // Detect post type from data attribute on posts container (for Taxi.js compatibility)
        const postsContainer = document.querySelector('[data-posts-container]');

        const detectedPostType = postsContainer?.dataset.postType || window.filterAjax?.post_type || 'cpt-articles';

        this.currentFilters = {
            post_type: detectedPostType,
            taxonomies: {},
            search: '',
            date: '',
            year: '',
            paged: 1,
            sort_by: 'date-desc',
            posts_per_page: (detectedPostType === 'cpt-articles' || detectedPostType === 'cpt-resources') ? 12 : 9,
            exclude_posts: []
        };
        this.isLoading = false;
        this.ajaxUrl = window.filterAjax?.ajaxurl || '/wp-admin/admin-ajax.php';
        this.nonce = window.filterAjax?.nonce || '';
        this.initialPostType = this.currentFilters.post_type; // Store initial post type
        this.getFeaturedPosts(); // Get featured post IDs to exclude
    }
    
    getFeaturedPosts() {
        // Get featured post IDs from data attributes on the page
        const postsContainer = document.querySelector('[data-posts-container]');
        if (postsContainer && postsContainer.dataset.excludePosts) {
            try {
                this.currentFilters.exclude_posts = JSON.parse(postsContainer.dataset.excludePosts);
            } catch (e) {
                console.error('Failed to parse exclude_posts:', e);
            }
        }
    }

    init() {
        // Check if we have any filtering elements on this page
        const hasSearch = document.querySelector('[data-search-input]');
        const hasTaxonomies = document.querySelector('[data-taxonomy]');
        const postsContainer = document.querySelector('[data-posts-container]');

        if (!hasSearch && !hasTaxonomies && !postsContainer) {
            return;
        }

        // Re-detect post type from DOM in case constructor ran before element existed
        if (postsContainer && postsContainer.dataset.postType) {
            this.currentFilters.post_type = postsContainer.dataset.postType;
            this.initialPostType = postsContainer.dataset.postType;
        }

        // Check if post type has changed and reset filters if needed
        this.checkPostTypeChange();
        
        // Listen for tab changes from taxonomies (blog, resources)
        const taxonomyContainers = document.querySelectorAll('[data-custom-pagination]');
        
        taxonomyContainers.forEach(container => {
            container.addEventListener('tabChanged', (event) => {
                const { index } = event.detail;
                this.switchTaxonomyGroup(index);
            });
        });
        
        // Initialize taxonomy filter buttons
        this.initTaxonomyButtons();
        
        // Initialize search integration
        this.initSearch();
        
        // Initialize clear button
        this.initClearButton();
        
        // Initialize sort dropdown
        this.initSortDropdown();
        
        // Handle browser back/forward
        this.initPopState();

        // Initialize from URL on page load
        this.initFromURL();

        // Initialize pagination handlers for existing pagination
        this.initPaginationHandlers();
    }
    
    checkPostTypeChange() {
        // Check DOM first, then fall back to window.filterAjax
        const postsContainer = document.querySelector('[data-posts-container]');
        const currentPostType = postsContainer?.dataset.postType || window.filterAjax?.post_type || 'cpt-articles';

        // If post type has changed, reset all filters
        if (this.initialPostType !== currentPostType) {
            this.resetFilters();
            this.initialPostType = currentPostType;
            this.currentFilters.post_type = currentPostType;
        }
    }

    resetFilters() {
        const postsContainer = document.querySelector('[data-posts-container]');
        const detectedPostType = postsContainer?.dataset.postType || window.filterAjax?.post_type || 'cpt-articles';

        this.currentFilters = {
            post_type: detectedPostType,
            taxonomies: {},
            search: '',
            date: '',
            year: '',
            paged: 1,
            sort_by: 'date-desc',
            posts_per_page: (detectedPostType === 'cpt-articles' || detectedPostType === 'cpt-resources') ? 12 : 9,
            exclude_posts: []
        };

        // Re-get featured posts after reset
        this.getFeaturedPosts();
        
        // Clear active filter buttons
        this.clearActiveFilters();
        
        // Clear search input
        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput) {
            searchInput.value = '';
        }
        
        // Clear URL parameters (but don't trigger history change)
        if (window.location.search) {
            const cleanURL = window.location.pathname;
            window.history.replaceState({ filters: this.currentFilters }, '', cleanURL);
        }
    }
    
    switchTaxonomyGroup(tabIndex) {
        const taxonomyGroups = document.querySelectorAll('[data-taxonomy-group]');
        
        // Hide all groups first
        taxonomyGroups.forEach(group => {
            gsap.to(group, { 
                opacity: 0, 
                duration: 0.2,
                onComplete: () => {
                    group.style.display = 'none';
                }
            });
        });
        
        // Show the selected group
        setTimeout(() => {
            const targetTaxonomy = tabIndex === 0 ? 'tax-news-type' : 'tax-news-topic';
            const targetGroup = document.querySelector(`[data-taxonomy-group="${targetTaxonomy}"]`);
            
            if (targetGroup) {
                targetGroup.style.display = 'flex';
                gsap.fromTo(targetGroup, 
                    { opacity: 0 }, 
                    { opacity: 1, duration: 0.3 }
                );
                
                // Clear any active filters when switching
                this.clearActiveFilters();
            }
        }, 200);
    }
    
    initTaxonomyButtons() {
        // Handle "Show all" buttons
        const showAllButtons = document.querySelectorAll('[data-show-all]');

        showAllButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const taxonomy = e.target.getAttribute('data-show-all');

                // Clear this specific taxonomy filter
                delete this.currentFilters.taxonomies[taxonomy];
                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        });
        
        // Handle button filters
        const taxonomyButtons = document.querySelectorAll('[data-taxonomy][data-term-id]');
        
        taxonomyButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const taxonomy = e.target.getAttribute('data-taxonomy');
                const termSlug = e.target.getAttribute('data-term-slug');
                
                // Toggle active state
                const isActive = e.target.classList.contains('active');
                
                if (isActive) {
                    this.removeFilter(e.target);
                } else {
                    this.addFilter(e.target, taxonomy, termSlug);
                }
            });
        });
        
        // Handle select dropdown filters
        const taxonomySelects = document.querySelectorAll('[data-taxonomy-select]');

        taxonomySelects.forEach(select => {
            select.addEventListener('change', (e) => {
                const taxonomy = e.target.getAttribute('data-taxonomy-select');
                const termSlug = e.target.value;
                const isParentFilter = e.target.hasAttribute('data-parent-filter');
                const isChildFilter = e.target.hasAttribute('data-child-filter');

                // Clear any active button filters for this taxonomy
                this.clearActiveFilters(taxonomy);

                if (termSlug) {
                    // Add/update this taxonomy filter
                    this.currentFilters.taxonomies[taxonomy] = termSlug;

                    // Store additional metadata for hierarchical taxonomies
                    if (taxonomy === 'tax-location') {
                        if (isParentFilter) {
                            this.currentFilters.locationFilterType = 'continent';
                        } else if (isChildFilter) {
                            this.currentFilters.locationFilterType = 'country';
                        }
                    }
                } else {
                    // Remove this taxonomy filter
                    delete this.currentFilters.taxonomies[taxonomy];

                    // Clear location filter type if it's a location filter
                    if (taxonomy === 'tax-location') {
                        delete this.currentFilters.locationFilterType;
                    }
                }

                // Reset to page 1 and execute filter
                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        });

        // Handle date filter dropdown
        const dateFilter = document.querySelector('[data-date-filter]');
        if (dateFilter) {
            dateFilter.addEventListener('change', (e) => {
                const dateValue = e.target.value;

                if (dateValue) {
                    this.currentFilters.date = dateValue;
                } else {
                    this.currentFilters.date = '';
                }

                // Reset to page 1 and execute filter
                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        }

        // Handle year filter dropdown for publications
        const yearFilter = document.querySelector('[data-year-filter]');
        if (yearFilter) {
            yearFilter.addEventListener('change', (e) => {
                const yearValue = e.target.value;

                if (yearValue) {
                    this.currentFilters.year = yearValue;
                } else {
                    this.currentFilters.year = '';
                }

                // Reset to page 1 and execute filter
                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        }

        // Handle post-type filter dropdown for search (URL-based, not AJAX)
        const postTypeFilter = document.querySelector('[data-post-type-select]');
        if (postTypeFilter) {
            postTypeFilter.addEventListener('change', (e) => {
                const postType = e.target.value;
                const urlParams = new URLSearchParams(window.location.search);
                const searchQuery = urlParams.get('s') || '';

                // Build new URL - always go to homepage
                let newUrl = '/';
                const params = new URLSearchParams();

                // Always add search query (even if empty)
                params.set('s', searchQuery);

                // Add post_type if selected
                if (postType) {
                    params.set('post_type', postType);
                }

                // Redirect to new URL with parameters
                newUrl += '?' + params.toString();

                window.location.href = newUrl;
            });
        }
    }
    
    initSearch() {
        const searchInput = document.querySelector('[data-search-input]');

        if (searchInput) {
            let searchTimeout;

            // Handle search input
            searchInput.addEventListener('input', (e) => {
                clearTimeout(searchTimeout);
                searchTimeout = setTimeout(() => {
                    this.currentFilters.search = e.target.value;
                    this.currentFilters.paged = 1;
                    this.executeFilter();
                }, 500);
            });
            
            // Handle Enter key
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    clearTimeout(searchTimeout);
                    this.currentFilters.search = e.target.value;
                    this.currentFilters.paged = 1;
                    this.executeFilter();
                }
            });
        }
        
        // Handle clear button
        const clearButton = document.querySelector('[data-search-clear]');
        if (clearButton) {
            clearButton.addEventListener('click', () => {
                const searchInput = document.querySelector('[data-search-input]');
                if (searchInput) {
                    searchInput.value = '';
                    this.currentFilters.search = '';
                    this.currentFilters.paged = 1;
                    this.executeFilter();
                }
            });
        }
    }
    
    initClearButton() {
        const clearButton = document.querySelector('[data-clear-filters]');
        if (clearButton) {
            clearButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearAllFilters();
            });
        }
    }
    
    clearAllFilters() {
        this.currentFilters.taxonomies = {};
        this.currentFilters.search = '';
        this.currentFilters.date = '';
        this.currentFilters.year = '';
        this.currentFilters.paged = 1;
        this.currentFilters.sort_by = 'date-desc';

        this.clearActiveFilters();

        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput) {
            searchInput.value = '';
        }

        const sortSelect = document.querySelector('[data-sort-select]');
        const sortLabel = document.querySelector('[data-sort-label]');

        if (sortSelect) {
            sortSelect.value = this.currentFilters.sort_by;
        } else if (sortLabel) {
            sortLabel.textContent = 'Sort by';
        }

        this.executeFilter();
    }
    
    initSortDropdown() {
        // Handle new select-based sort dropdown
        const sortSelect = document.querySelector('[data-sort-select]');
        
        if (sortSelect) {
            sortSelect.addEventListener('change', (e) => {
                this.currentFilters.sort_by = e.target.value;
                this.currentFilters.paged = 1; // Reset to first page
                this.executeFilter();
            });
            return;
        }
        
        // Fallback to old dropdown method
        const sortTrigger = document.querySelector('[data-sort-trigger]');
        const sortMenu = document.querySelector('[data-sort-menu]');
        const sortIcon = document.querySelector('[data-sort-icon]');
        const sortLabel = document.querySelector('[data-sort-label]');
        const sortOptions = document.querySelectorAll('[data-sort-option]');
        
        if (!sortTrigger || !sortMenu) return;
        
        // Toggle dropdown
        sortTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const isOpen = !sortMenu.classList.contains('hidden');
            
            if (isOpen) {
                this.closeSortDropdown();
            } else {
                this.openSortDropdown();
            }
        });
        
        // Handle sort option clicks
        sortOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                e.preventDefault();
                const sortValue = option.getAttribute('data-sort-option');
                const sortText = option.textContent.trim();
                
                // Update filter and UI
                this.currentFilters.sort_by = sortValue;
                this.currentFilters.paged = 1; // Reset to first page
                
                // Update button text
                if (sortLabel) {
                    sortLabel.textContent = sortText;
                }
                
                // Close dropdown
                this.closeSortDropdown();
                
                // Execute filter
                this.executeFilter();
            });
        });
        
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            const sortDropdown = document.querySelector('[data-sort-dropdown]');
            if (sortDropdown && !sortDropdown.contains(e.target)) {
                this.closeSortDropdown();
            }
        });
    }
    
    openSortDropdown() {
        const sortMenu = document.querySelector('[data-sort-menu]');
        const sortIcon = document.querySelector('[data-sort-icon]');
        
        if (sortMenu) {
            sortMenu.classList.remove('hidden');
        }
        if (sortIcon) {
            sortIcon.style.transform = 'rotate(180deg)';
        }
    }
    
    closeSortDropdown() {
        const sortMenu = document.querySelector('[data-sort-menu]');
        const sortIcon = document.querySelector('[data-sort-icon]');
        
        if (sortMenu) {
            sortMenu.classList.add('hidden');
        }
        if (sortIcon) {
            sortIcon.style.transform = 'rotate(0deg)';
        }
    }
    
    initPopState() {
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.filters) {
                this.currentFilters = { ...e.state.filters };
                this.executeFilter(false); // Don't update URL
                this.updateUIFromFilters();
            }
        });
    }
    
    initFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const search = urlParams.get('search');
        const date = urlParams.get('date');
        const year = urlParams.get('year');
        const paged = parseInt(urlParams.get('paged')) || 1;

        // Check for taxonomy-specific query parameters
        const typeSlug = urlParams.get('type');
        const topicSlug = urlParams.get('topic');
        const locationSlug = urlParams.get('location');
        const categorySlug = urlParams.get('category');
        const serviceSlug = urlParams.get('service');
        const continentSlug = urlParams.get('continent');
        const countrySlug = urlParams.get('country');

        if (typeSlug) {
            if (this.currentFilters.post_type === 'cpt-events') {
                this.currentFilters.taxonomies['tax-event-type'] = typeSlug;
            } else if (this.currentFilters.post_type === 'cpt-articles') {
                this.currentFilters.taxonomies['tax-news-type'] = typeSlug;
            } else if (this.currentFilters.post_type === 'cpt-resources') {
                this.currentFilters.taxonomies['tax-resource-type'] = typeSlug;
            }
        }

        // Handle vacancy-specific parameters
        const roleSlug = urlParams.get('role');
        const departmentSlug = urlParams.get('department');
        const vacancyCountrySlug = urlParams.get('country');

        if (roleSlug && this.currentFilters.post_type === 'cpt-vacancies') {
            this.currentFilters.taxonomies['tax-vacancy-role'] = roleSlug;
        }
        if (departmentSlug && this.currentFilters.post_type === 'cpt-vacancies') {
            this.currentFilters.taxonomies['tax-vacancy-department'] = departmentSlug;
        }
        if (vacancyCountrySlug && this.currentFilters.post_type === 'cpt-vacancies') {
            this.currentFilters.taxonomies['tax-vacancy-country'] = vacancyCountrySlug;
        }

        if (topicSlug) {
            if (this.currentFilters.post_type === 'cpt-events') {
                this.currentFilters.taxonomies['tax-event-type'] = topicSlug;
            } else if (this.currentFilters.post_type === 'cpt-articles') {
                this.currentFilters.taxonomies['tax-news-topic'] = topicSlug;
            } else if (this.currentFilters.post_type === 'cpt-resources') {
                this.currentFilters.taxonomies['tax-resource-topic'] = topicSlug;
            }
        }
        if (categorySlug && this.currentFilters.post_type === 'cpt-resources') {
            this.currentFilters.taxonomies['tax-resource-type'] = categorySlug;
        }
        if (serviceSlug && this.currentFilters.post_type === 'cpt-case_studies') {
            this.currentFilters.taxonomies['tax-case-study-service'] = serviceSlug;
        }
        if (locationSlug && this.currentFilters.post_type === 'cpt-events') {
            this.currentFilters.taxonomies['tax-location'] = locationSlug;
        }
        if (continentSlug && this.currentFilters.post_type === 'cpt-events') {
            this.currentFilters.taxonomies['tax-location'] = continentSlug;
        }
        if (countrySlug && this.currentFilters.post_type === 'cpt-events') {
            this.currentFilters.taxonomies['tax-location'] = countrySlug;
        }

        if (search) {
            this.currentFilters.search = search;
        }
        if (date) {
            this.currentFilters.date = date;
        }
        if (year) {
            this.currentFilters.year = year;
        }
        this.currentFilters.paged = paged;

        if (Object.keys(this.currentFilters.taxonomies).length > 0 || search || date || year || paged > 1) {
            this.executeFilter(false); // Don't update URL on init
            this.updateUIFromFilters();
        }
    }
    
    addFilter(button, taxonomy, termSlug) {
        // Add/update this taxonomy filter
        this.currentFilters.taxonomies[taxonomy] = termSlug;
        this.currentFilters.paged = 1; // Reset to first page
        this.executeFilter();
    }

    removeFilter(button) {
        const taxonomy = button.getAttribute('data-taxonomy');

        // Remove this taxonomy filter
        if (taxonomy) {
            delete this.currentFilters.taxonomies[taxonomy];
        }
        this.currentFilters.paged = 1;
        this.executeFilter();
    }
    
    clearActiveFilters(specificTaxonomy = null) {
        // Clear button filters
        const activeButtons = specificTaxonomy
            ? document.querySelectorAll(`[data-taxonomy="${specificTaxonomy}"].active`)
            : document.querySelectorAll('[data-taxonomy].active');

        // Clear select dropdowns
        const taxonomySelects = specificTaxonomy
            ? document.querySelectorAll(`[data-taxonomy-select="${specificTaxonomy}"]`)
            : document.querySelectorAll('[data-taxonomy-select]');
            
        taxonomySelects.forEach(select => {
            select.value = '';
        });
    }
    
    updateUIFromFilters() {
        // Update active filter buttons for each taxonomy
        Object.entries(this.currentFilters.taxonomies).forEach(([taxonomy, termSlug]) => {
            const activeButton = document.querySelector(
                `[data-taxonomy="${taxonomy}"][data-term-slug="${termSlug}"]`
            );

            // Update select dropdowns - special handling for tax-location
            if (taxonomy === 'tax-location') {
                // Check URL params to determine if it's continent or country
                const urlParams = new URLSearchParams(window.location.search);
                const continentParam = urlParams.get('continent');
                const countryParam = urlParams.get('country');

                if (continentParam) {
                    const continentSelect = document.getElementById('event-continent');
                    if (continentSelect) {
                        continentSelect.value = continentParam;
                    }
                }
                if (countryParam) {
                    const countrySelect = document.getElementById('event-country');
                    if (countrySelect) {
                        countrySelect.value = countryParam;
                    }
                }
            } else {
                const select = document.querySelector(`[data-taxonomy-select="${taxonomy}"]`);
                if (select) {
                    select.value = termSlug;
                }
            }
        });

        // Update search input
        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput && this.currentFilters.search) {
            searchInput.value = this.currentFilters.search;
        }

        // Update date filter if present
        const dateFilter = document.querySelector('[data-date-filter]');
        if (dateFilter && this.currentFilters.date) {
            dateFilter.value = this.currentFilters.date;
        }

        // Update year filter if present
        const yearFilter = document.querySelector('[data-year-filter]');
        if (yearFilter && this.currentFilters.year) {
            yearFilter.value = this.currentFilters.year;
        }
    }
    
    executeFilter(updateURL = true) {
        if (this.isLoading) return;

        this.isLoading = true;
        this.showLoadingState();

        const formData = new FormData();
        formData.append('action', 'filter_posts');
        formData.append('nonce', this.nonce);
        formData.append('post_type', this.currentFilters.post_type);
        formData.append('search', this.currentFilters.search);
        formData.append('date', this.currentFilters.date);
        formData.append('year', this.currentFilters.year);
        formData.append('paged', this.currentFilters.paged);
        formData.append('sort_by', this.currentFilters.sort_by);
        formData.append('posts_per_page', this.currentFilters.posts_per_page);

        // Add multiple taxonomies as JSON
        formData.append('taxonomies', JSON.stringify(this.currentFilters.taxonomies));

        // Add excluded post IDs (featured posts)
        if (this.currentFilters.exclude_posts && this.currentFilters.exclude_posts.length > 0) {
            this.currentFilters.exclude_posts.forEach(id => {
                formData.append('exclude_posts[]', id);
            });
        }
        
        fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.updateResults(data.data);
                if (updateURL) {
                    this.updateURL();
                }
                // Update UI to reflect current filters
                this.updateUIFromFilters();
            } else {
                console.error('Filter request failed:', data);
            }
        })
        .catch(error => {
            console.error('AJAX error:', error);
        })
        .finally(() => {
            this.isLoading = false;
            this.hideLoadingState();
        });
    }
    
    showLoadingState() {
        const container = document.querySelector('[data-posts-container]');
        if (container) {
            container.style.opacity = '0.5';
            container.style.pointerEvents = 'none';
        }
    }
    
    hideLoadingState() {
        const container = document.querySelector('[data-posts-container]');
        if (container) {
            gsap.to(container, { opacity: 1, duration: 0.3 });
            container.style.pointerEvents = 'auto';
        }
    }
    
    updateResults(data) {
        const container = document.querySelector('[data-posts-container]');
        if (!container) return;

        // Fade out current content
        gsap.to(container, {
            opacity: 0,
            duration: 0.2,
            onComplete: () => {
                // Replace content
                container.innerHTML = data.posts.join('');

                // Update results count
                const resultsNumber = document.querySelector('[data-results-number]');
                if (resultsNumber && data.pagination) {
                    resultsNumber.textContent = data.pagination.total_posts;
                }

                // Update pagination if exists
                this.updatePagination(data.pagination);

                // Fade in new content
                gsap.fromTo(container,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.3 }
                );
            }
        });
    }
    
    updatePagination(pagination) {
        const paginationContainer = document.querySelector('[data-pagination-container]');

        if (!paginationContainer) return;

        // If only one page or no pages, hide pagination
        if (!pagination || pagination.total_pages <= 1) {
            paginationContainer.innerHTML = '';
            return;
        }

        const currentPage = pagination.current_page;
        const totalPages = pagination.total_pages;
        const showDots = totalPages > 5;

        let html = '';

        // Previous button
        if (currentPage > 1) {
            html += `<a href="#" data-page="${currentPage - 1}" class="flex h-12 w-12 justify-center p-xs rounded-xxl border-0 outline-0 bg-blue-1 items-center cursor-pointer hover:bg-blue-2 transition">
                <div class="w-4 h-4 rotate-180 text-white">
                    <svg width="7" height="10" viewBox="0 0 7 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 9L5 5L1 1" stroke-width="2"></path>
                    </svg>
                </div>
            </a>`;
        }

        // Calculate range
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, currentPage + 2);

        // First page + dots
        if (showDots && start > 1) {
            html += `<a href="#" data-page="1" class="flex h-12 w-12 justify-center rounded-xxl border-0 outline-0 items-center cursor-pointer ${currentPage === 1 ? 'bg-purple hover:bg-blue-2 transition' : 'border border-grey-2 bg-transparent hover:bg-grey-2 transition'}">
                <p class="${currentPage === 1 ? 'text-white' : 'text-blue-1'} leading-none text-center">1</p>
            </a>`;
            if (start > 2) {
                html += `<div class="flex h-12 w-12 justify-center rounded-xxl border border-grey-2 outline-0 bg-transparent items-center">
                    <p class="text-blue-1 leading-none text-center">...</p>
                </div>`;
            }
        }

        // Page numbers
        for (let i = start; i <= end; i++) {
            html += `<a href="#" data-page="${i}" class="flex h-12 w-12 justify-center rounded-xxl border-0 outline-0 items-center cursor-pointer ${currentPage === i ? 'bg-purple hover:bg-blue-2 transition' : 'border border-grey-2 bg-transparent hover:bg-grey-2 transition'}">
                <p class="${currentPage === i ? 'text-white' : 'text-blue-1'} leading-none text-center">${i}</p>
            </a>`;
        }

        // Dots + last page
        if (showDots && end < totalPages) {
            if (end < totalPages - 1) {
                html += `<div class="flex h-12 w-12 justify-center rounded-xxl border border-grey-2 outline-0 bg-transparent items-center">
                    <p class="text-blue-1 leading-none text-center">...</p>
                </div>`;
            }
            html += `<a href="#" data-page="${totalPages}" class="flex h-12 w-12 justify-center rounded-xxl border-0 outline-0 items-center cursor-pointer ${currentPage === totalPages ? 'bg-purple hover:bg-blue-2 transition' : 'border border-grey-2 bg-transparent hover:bg-grey-2 transition'}">
                <p class="${currentPage === totalPages ? 'text-white' : 'text-blue-1'} leading-none text-center">${totalPages}</p>
            </a>`;
        }

        // Next button
        if (currentPage < totalPages) {
            html += `<a href="#" data-page="${currentPage + 1}" class="flex h-12 w-12 justify-center p-xs rounded-xxl border-0 outline-0 bg-blue-1 items-center cursor-pointer hover:bg-blue-2 transition">
                <div class="w-4 h-4 text-white">
                    <svg width="7" height="10" viewBox="0 0 7 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 9L5 5L1 1" stroke-width="2"></path>
                    </svg>
                </div>
            </a>`;
        }

        paginationContainer.innerHTML = html;

        // Add click handlers to new pagination links
        this.initPaginationHandlers();
    }

    initPaginationHandlers() {
        const paginationContainer = document.querySelector('[data-pagination-container]');

        if (!paginationContainer) return;

        // Remove old listener if it exists
        if (this.paginationClickHandler) {
            paginationContainer.removeEventListener('click', this.paginationClickHandler);
        }

        // Create new handler with event delegation
        this.paginationClickHandler = (e) => {
            // Check if clicked element or its parent is a pagination link
            const link = e.target.closest('[data-page]');

            if (link && link.hasAttribute('data-page')) {
                e.preventDefault();
                const page = parseInt(link.getAttribute('data-page'));

                if (page && page !== this.currentFilters.paged) {
                    this.currentFilters.paged = page;
                    this.executeFilter();

                    // Scroll to top of parent container
                    const postsContainer = document.querySelector('[data-posts-container]');
                    if (postsContainer) {
                        const parentContainer = postsContainer.closest('.container');
                        if (parentContainer) {
                            const offset = 100; // Offset for header
                            const elementPosition = parentContainer.getBoundingClientRect().top + window.pageYOffset;
                            window.scrollTo({
                                top: elementPosition - offset,
                                behavior: 'smooth'
                            });
                        }
                    }
                }
            }
        };

        // Add event delegation listener to container
        paginationContainer.addEventListener('click', this.paginationClickHandler);
    }
    
    updateURL() {
        const params = new URLSearchParams();

        // Add all taxonomy filters to URL
        Object.entries(this.currentFilters.taxonomies).forEach(([taxonomy, termSlug]) => {
            // All "type" taxonomies use ?type=
            if (taxonomy.includes('-type')) {
                params.set('type', termSlug);
            }
            // All "topic" taxonomies use ?topic=
            else if (taxonomy.includes('-topic')) {
                params.set('topic', termSlug);
            }
            // Case study service taxonomy uses ?service=
            else if (taxonomy === 'tax-case-study-service') {
                params.set('service', termSlug);
            }
            // Location taxonomy uses ?continent= or ?country= for hierarchical
            else if (taxonomy === 'tax-location') {
                if (this.currentFilters.locationFilterType === 'continent') {
                    params.set('continent', termSlug);
                } else if (this.currentFilters.locationFilterType === 'country') {
                    params.set('country', termSlug);
                } else {
                    // Fallback to location if type unknown
                    params.set('location', termSlug);
                }
            }
            // Vacancy taxonomies
            else if (taxonomy === 'tax-vacancy-role') {
                params.set('role', termSlug);
            }
            else if (taxonomy === 'tax-vacancy-department') {
                params.set('department', termSlug);
            }
            else if (taxonomy === 'tax-vacancy-country') {
                params.set('country', termSlug);
            }
        });

        if (this.currentFilters.search) {
            params.set('search', this.currentFilters.search);
        }
        if (this.currentFilters.date) {
            params.set('date', this.currentFilters.date);
        }
        if (this.currentFilters.year) {
            params.set('year', this.currentFilters.year);
        }
        if (this.currentFilters.paged > 1) {
            params.set('paged', this.currentFilters.paged);
        }

        const newURL = window.location.pathname + (params.toString() ? '?' + params.toString() : '');

        // Push state for browser history
        window.history.pushState(
            { filters: this.currentFilters },
            '',
            newURL
        );
    }
}